'use client';

import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { ArrowLeft, Bell, Bookmark, Clock3, FileText, LogOut, MessageCircle, Settings, UserRound } from 'lucide-react';
import CommentLogin, { type DemoUser } from './comment-login';
import { clearWikiUser, readWikiToken, readWikiUser, WIKI_AUTH_EVENT, writeWikiUser } from '../lib/wiki-auth';
import { refreshWikiSession } from '../lib/gamekee-api';
import { fetchGames } from '../lib/game-config';
import { shareAsync } from '../lib/share-async';
import { wikiGameHomePath, wikiGuidePostPath } from '../lib/spa-routes';
import './profile-page.css';

type Section = 'messages' | 'history' | 'comments' | 'posts' | 'favorites' | 'settings';
type HistoryItem = { id: string; gameId: string; name: string; subtitle: string; head: string; visitedAt: number };
type ActivityPost={gameKey:string;id:string;type:string;title:string;excerpt:string;status:string;updatedAt:string};
type ActivityComment={gameKey:string;id:string;targetType:string;targetKey:string;content:string;createdAt:string};
type ActivityItem={gameKey:string;targetType:string;targetKey:string;title:string;subtitle:string;image?:string;url:string;createdAt?:string;viewedAt?:string};
type ActivityNotification={gameKey:string;id:string;type:string;actor?:string;title:string;body:string;url?:string;readAt?:string;createdAt:string};
type ActivityPayload = {
  posts: ActivityPost[];
  comments: ActivityComment[];
  favorites: ActivityItem[];
  history: ActivityItem[];
  notifications: ActivityNotification[];
};

const menu: Array<[Section, string, typeof Bell]> = [
  ['messages', '消息', Bell], ['history', '浏览记录', Clock3], ['comments', '我的评论', MessageCircle],
  ['posts', '我的发帖', FileText], ['favorites', '我的收藏', Bookmark], ['settings', '资料修改', Settings],
];

const activityInflight = new Map<string, Promise<ActivityPayload>>();

function fetchMyActivity() {
  const token = readWikiToken() || '';
  return shareAsync(activityInflight, token || 'anon', async () => {
    const response = await fetch('/api/me/activity', {
      cache: 'no-store',
      headers: token ? { 'access-token': token } : {},
    });
    const payload = (await response.json()) as ActivityPayload & { error?: string };
    if (!response.ok) throw new Error(payload.error || '个人内容加载失败');
    return {
      posts: Array.isArray(payload.posts) ? payload.posts : [],
      comments: Array.isArray(payload.comments) ? payload.comments : [],
      favorites: Array.isArray(payload.favorites) ? payload.favorites : [],
      history: Array.isArray(payload.history) ? payload.history : [],
      notifications: Array.isArray(payload.notifications) ? payload.notifications : [],
    };
  }, 4000);
}

function userIdentity(user: DemoUser | null) {
  if (!user) return '';
  return String(user.uid ?? user.name ?? '');
}

export default function ProfilePage({
  gameKey = 'arcanatactics',
}: {
  gameKey?: string;
}) {
  const base = `/${gameKey}`;
  const [section, setSection] = useState<Section>('messages');
  const [user, setUser] = useState<DemoUser | null>(null);
  const [nickname, setNickname] = useState('');
  const [avatar, setAvatar] = useState('');
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [ready, setReady] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [myPosts, setMyPosts] = useState<ActivityPost[]>([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsError, setPostsError] = useState('');
  const [myComments, setMyComments] = useState<ActivityComment[]>([]);
  const [myFavorites, setMyFavorites] = useState<ActivityItem[]>([]);
  const [accountHistory,setAccountHistory]=useState<ActivityItem[]>([]);
  const [notifications,setNotifications]=useState<ActivityNotification[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState('');
  const [domainByKey, setDomainByKey] = useState<Record<string, string>>({});
  const avatarInput = useRef<HTMLInputElement>(null);
  const loadedActivityUid = useRef('');

  useEffect(() => {
    let cancelled = false;
    void fetchGames(true)
      .then((list) => {
        if (cancelled) return;
        const next: Record<string, string> = {};
        for (const game of list) {
          next[game.key] = game.domain || game.key;
          if (game.domain) next[game.domain] = game.domain;
        }
        setDomainByKey(next);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const resolveDomain = (key: string) => domainByKey[key] || key;

  useEffect(() => {
    try {
      const requestedSection = new URLSearchParams(window.location.search).get('section');
      if (menu.some(([id]) => id === requestedSection)) setSection(requestedSection as Section);
      const nextUser = readWikiUser();
      setUser(nextUser);
      setNickname(localStorage.getItem('hero-archive-profile-nickname') || nextUser?.name || '');
      setAvatar(localStorage.getItem('hero-archive-profile-avatar') || nextUser?.avatar || '');
      setHistory(JSON.parse(localStorage.getItem('hero-archive-view-history') || '[]'));
      setLoginOpen(!nextUser);
      setReady(true);
      refreshWikiSession().catch(() => {});
    } catch { setReady(true); setLoginOpen(true); }
    const sync = () => {
      const next = readWikiUser();
      setUser((current) => {
        if (userIdentity(current) === userIdentity(next) && Boolean(current) === Boolean(next)) {
          return current;
        }
        return next;
      });
      if (next) {
        setNickname(localStorage.getItem('hero-archive-profile-nickname') || next.name || '');
        setAvatar(localStorage.getItem('hero-archive-profile-avatar') || next.avatar || '');
      } else setLoginOpen(true);
    };
    window.addEventListener('storage', sync);
    window.addEventListener(WIKI_AUTH_EVENT, sync);
    return () => { window.removeEventListener('storage', sync); window.removeEventListener(WIKI_AUTH_EVENT, sync); };
  }, []);

  useEffect(() => {
    if (!user) {
      loadedActivityUid.current = '';
      return;
    }
    const uid = userIdentity(user);
    if (loadedActivityUid.current === uid) return;
    loadedActivityUid.current = uid;
    let cancelled = false;
    setPostsLoading(true);
    setActivityLoading(true);
    setPostsError('');
    setActivityError('');
    fetchMyActivity()
      .then((result) => {
        if (cancelled) return;
        setMyPosts(result.posts);
        setMyComments(result.comments);
        setMyFavorites(result.favorites);
        setAccountHistory(result.history);
        setNotifications(result.notifications);
      })
      .catch((error) => {
        if (cancelled) return;
        loadedActivityUid.current = '';
        const message = error instanceof Error ? error.message : '个人内容加载失败';
        setPostsError(message);
        setActivityError(message);
      })
      .finally(() => {
        if (!cancelled) {
          setPostsLoading(false);
          setActivityLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const chooseAvatar = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file?.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => { const value = String(reader.result || ''); setAvatar(value); localStorage.setItem('hero-archive-profile-avatar', value); if (user) writeWikiUser({ ...user, avatar: value }); };
    reader.readAsDataURL(file);
  };
  const saveNickname = () => {
    const value = nickname.trim();
    if (!value || !user) return;
    const next = { ...user, name: value };
    setUser(next);
    setNickname(value);
    writeWikiUser(next);
    localStorage.setItem('hero-archive-profile-nickname', value);
  };
  const signOut = () => {
    clearWikiUser();
    window.location.href = '/';
  };
  const deleteAccount = () => {
    ['hero-archive-profile-nickname', 'hero-archive-profile-avatar'].forEach(key => localStorage.removeItem(key));
    clearWikiUser();
    window.location.href = '/';
  };

  if (!ready) return null;
  if (!user) return <main className="profile-page"><CommentLogin open={loginOpen} onOpenChange={open => { setLoginOpen(open); if (!open) window.location.href = '/'; }} onLogin={next => { writeWikiUser(next); setUser(next); setNickname(next.name); setAvatar(next.avatar || ''); setLoginOpen(false); }}/></main>;

  return <main className="profile-page">
    <header className="profile-page-header">
      <a href={`${base}/dcatalog`} aria-label="返回英雄图鉴"><ArrowLeft/>返回图鉴</a>
      <div><span>PLAYER CENTER</span><h1>个人中心</h1></div>
    </header>
    <div className="profile-page-layout">
      <aside className="profile-page-sidebar">
        <section className="profile-page-user">
          <div>{avatar ? <img src={avatar} alt="我的头像"/> : <UserRound/>}</div>
          <h2>{user?.name || '未登录'}</h2>
          <p>UID：{user.uid ?? '—'}</p>
          <small>{user.method === 'phone' ? '手机号登录' : user.method === 'email' ? '邮箱登录' : 'Wiki 账号'}</small>
        </section>
        <nav aria-label="个人中心功能">{menu.map(([id, label, Icon]) => <button key={id} className={section === id ? 'active' : ''} onClick={() => setSection(id)}><Icon/><span>{label}</span></button>)}</nav>
        {user && <button className="profile-page-signout" onClick={signOut}><LogOut/>退出登录</button>}
      </aside>
      <section className="profile-page-content">
        {section === 'messages' && <><header><div><span>NOTIFICATIONS</span><h2>消息</h2></div><b>{notifications.filter(item=>!item.readAt).length} 条未读</b></header>{notifications.length?<div className="profile-activity-list">{notifications.map(item=><a href={item.url||'#'} key={item.id}><div><strong>{item.title}</strong><p>{item.body}</p><small>{new Date(item.createdAt).toLocaleString('zh-CN')}</small></div></a>)}</div>:<Empty icon={Bell} title="暂时没有未读消息" text="别人回复或点赞你的内容后，会优先显示在这里。"/>}</>}
        {section === 'history' && <><header><div><span>RECENTLY VIEWED</span><h2>浏览记录</h2></div><b>最近 {accountHistory.length||history.length} 项</b></header>{accountHistory.length?<ActivityList items={accountHistory}/>:history.length?<div className="profile-history">{history.map(item => <a key={`${item.gameId}:${item.id}`} href={`/${item.gameId}/t${item.id}`}><img src={item.head} alt=""/><span><strong>{item.name}</strong><small>{item.subtitle}</small></span><time>{new Date(item.visitedAt).toLocaleDateString('zh-CN')}</time></a>)}</div>:<Empty icon={Clock3} title="暂无浏览记录" text="你查看过的内容会自动记录在这里。"/>}</>}
        {section === 'comments' && <><header><div><span>MY ACTIVITY</span><h2>我的评论</h2></div><b>{myComments.length} 条</b></header>{activityLoading ? <Empty icon={MessageCircle} title="正在加载评论" text="正在从 GameKee 获取你的评论。"/> : activityError ? <Empty icon={MessageCircle} title="评论加载失败" text={activityError}/> : myComments.length ? <CommentList comments={myComments} resolveDomain={resolveDomain}/> : <Empty icon={MessageCircle} title="还没有发表过评论" text="你发表的评论会集中显示在这里。"/>}</>}
        {section === 'posts' && <><header><div><span>MY POSTS</span><h2>我的帖子</h2></div><b>{myPosts.length} 篇</b></header>{postsLoading ? <Empty icon={FileText} title="正在加载帖子" text="正在从 GameKee 获取你的内容。"/> : postsError ? <Empty icon={FileText} title="帖子加载失败" text={postsError}/> : myPosts.length ? <PostList posts={myPosts} resolveDomain={resolveDomain}/> : <Empty icon={FileText} title="还没有发布过帖子" text="你发布的内容会集中显示在这里。"/>}</>}
        {section === 'favorites' && <><header><div><span>SAVED</span><h2>我的收藏</h2></div><b>{myFavorites.length} 项</b></header>{activityLoading ? <Empty icon={Bookmark} title="正在加载收藏" text="正在从 GameKee 获取你的收藏。"/> : activityError ? <Empty icon={Bookmark} title="收藏加载失败" text={activityError}/> : myFavorites.length ? <FavoriteList favorites={myFavorites}/> : <Empty icon={Bookmark} title="还没有收藏帖子" text="你在 GameKee 收藏的帖子会显示在这里。"/>}</>}
        {section === 'settings' && <><header><div><span>ACCOUNT SETTINGS</span><h2>资料修改</h2></div></header><div className="profile-settings"><label>头像</label><div className="profile-avatar-edit"><div>{avatar ? <img src={avatar} alt="我的头像"/> : <UserRound/>}</div><button onClick={() => avatarInput.current?.click()}>更换头像</button><input ref={avatarInput} type="file" accept="image/*" hidden onChange={chooseAvatar}/></div><label htmlFor="profile-page-nickname">昵称</label><div className="profile-nickname-edit"><input id="profile-page-nickname" value={nickname} maxLength={24} onChange={e => setNickname(e.target.value)} placeholder="输入昵称"/><button onClick={saveNickname} disabled={!user || !nickname.trim()}>保存</button></div><div className="profile-danger"><div><strong>注销账号</strong><span>清除当前演示账号与个人资料</span></div><button onClick={deleteAccount}>注销</button></div></div></>}
      </section>
    </div>
  </main>;
}

function Empty({ icon: Icon, title, text }: { icon: typeof Bell; title: string; text: string }) {
  return <div className="profile-page-empty"><Icon/><strong>{title}</strong><p>{text}</p></div>;
}

function PostList({
  posts,
  resolveDomain,
}: {
  posts: ActivityPost[];
  resolveDomain: (key: string) => string;
}) {
  return <div className="profile-post-list">{posts.map(post => <a href={wikiGuidePostPath(resolveDomain(post.gameKey), post.id)} key={post.id}>
    <div><strong>{post.title || '未命名帖子'}</strong>{post.excerpt&&<p>{post.excerpt}</p>}<small>{new Date(post.updatedAt).toLocaleDateString('zh-CN')} · {post.status==='published'?'已发布':post.status==='pending'?'待审核':'草稿'}</small></div>
  </a>)}</div>;
}

function plainText(value: string) {
  return value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
}

function CommentList({
  comments,
  resolveDomain,
}: {
  comments: ActivityComment[];
  resolveDomain: (key: string) => string;
}) {
  return <div className="profile-activity-list">{comments.map(comment => <a href={wikiGameHomePath(resolveDomain(comment.gameKey))} key={comment.id}>
    <div><strong>我的评论</strong><p>{plainText(comment.content)}</p><small>{new Date(comment.createdAt).toLocaleString('zh-CN')}</small></div>
  </a>)}</div>;
}

function FavoriteList({ favorites }: { favorites: ActivityItem[] }) { return <ActivityList items={favorites}/>; }
function ActivityList({items}:{items:ActivityItem[]}){
  return <div className="profile-post-list">{items.map(item=><a href={item.url} key={`${item.gameKey}:${item.targetType}:${item.targetKey}`}>{item.image&&<img src={item.image} alt=""/>}<div><strong>{item.title}</strong>{item.subtitle&&<p>{item.subtitle}</p>}<small>{item.gameKey} · {new Date(item.viewedAt||item.createdAt||0).toLocaleDateString('zh-CN')}</small></div>
  </a>)}</div>;
}
